import { combineReducers } from 'redux';
import filter from 'lodash/filter';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import without from 'lodash/without';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';

import { eventsDuck, locationDuck } from '@eb/redux-destination';

import { FOR_YOU } from '../../constants/tabConfig';

import {
    UPDATE_LIKES_EVENT_BUCKET,
    UPDATE_EVENT_SAVED_BY_YOU,
    UPDATE_TAB_CONTENT,
} from '../events/actions';
import {
    UPDATE_CONTENT,
    UPDATE_TAB_KEY,
    SET_EDITORIAL_COLLECTIONS,
} from './actions';
import { LIKED_EVENTS } from '../../constants/constants';

import { getSeasonalContent } from '../../utils/seasonal';

const {
    actionTypes: { UPDATE_COLLECTION_EVENTS },
} = eventsDuck;

const {
    actionTypes: { UPDATE_LOCATION },
} = locationDuck;

// eslint-disable-next-line
const entityContext = (state = [], { type, payload }) => {
    let nextState = state;

    if (type === UPDATE_EVENT_SAVED_BY_YOU) {
        const { eventId, savedByYou } = payload;

        nextState = map(nextState, (bucket) => {
            // special case for liked_events bucket
            if (bucket.key === LIKED_EVENTS) {
                if (savedByYou) {
                    return {
                        ...bucket,
                        events: uniq([eventId, ...bucket.events]),
                    };
                }
                return {
                    ...bucket,
                    // eslint-disable-next-line no-shadow
                    events: filter(bucket.events, (event) => event !== eventId),
                };
            }

            return bucket;
        });
    }

    if (type === UPDATE_LIKES_EVENT_BUCKET) {
        const { eventIds } = payload;

        nextState = map(nextState, (bucket) => {
            // special case for liked_events bucket
            if (bucket.key === LIKED_EVENTS) {
                return {
                    ...bucket,
                    events: uniq([...bucket.events, ...eventIds]),
                };
            }

            return bucket;
        });
    }

    if (type === UPDATE_CONTENT) {
        // remove all the buckets but liked_events bucket
        const likedEventsBucket = filter(nextState, { key: LIKED_EVENTS });

        nextState = [...likedEventsBucket, ...payload.entityContext];
    }

    return nextState;
};

// eslint-disable-next-line
const entities = (state = {}, { type, payload }) => {
    let nextState = state;

    if (type === UPDATE_EVENT_SAVED_BY_YOU) {
        const { eventId, savedByYou } = payload;

        if (nextState[eventId]) {
            nextState = {
                ...nextState,
                [eventId]: {
                    ...nextState[eventId],
                    saves: {
                        savedByYou,
                    },
                },
            };
        }
    }

    if (type === UPDATE_LIKES_EVENT_BUCKET) {
        const { events } = payload;

        nextState = {
            ...nextState,
            ...events,
        };
    }

    if (type === UPDATE_CONTENT) {
        nextState = {
            ...nextState,
            ...payload.entities,
        };
    }

    if (type === UPDATE_COLLECTION_EVENTS) {
        if (nextState[payload.eventId]) {
            nextState = {
                ...nextState,
                [payload.eventId]: {
                    ...nextState[payload.eventId],
                    eventCollections: payload.shouldAdd
                        ? [
                              ...nextState[payload.eventId].eventCollections,
                              payload.collectionId,
                          ]
                        : without(
                              nextState[payload.eventId].eventCollections,
                              payload.collectionId,
                          ),
                },
            };
        }
    }

    return nextState;
};

const flatBucket = (state = [], { type, payload }) => {
    let nextState = state;

    if (type === UPDATE_TAB_CONTENT) {
        const results = payload.events.results;
        const eventSearch = payload.eventSearch;
        const follow = payload.follow;
        const hasFiveToFollow = payload.hasFiveToFollow;

        nextState = {
            results,
            eventSearch,
            follow,
            hasFiveToFollow,
        };
    }

    if (type === UPDATE_EVENT_SAVED_BY_YOU) {
        const { results } = nextState;
        const { eventId, tabKey, savedByYou } = payload;

        if (!isEmpty(results)) {
            const _results = results.map((event) => {
                let _savedByYou = {};

                if (event.id === eventId) {
                    _savedByYou = {
                        saves: {
                            saved_by_you: savedByYou,
                        },
                    };
                }
                return {
                    ...event,
                    ..._savedByYou,
                };
            });

            let filteredEvents = _results;

            if (tabKey && tabKey === FOR_YOU) {
                filteredEvents = _results.filter(
                    (event) => !savedByYou && event.id !== eventId,
                );
            }

            nextState = {
                ...nextState,
                results: filteredEvents,
            };
        }
    }

    if (type === UPDATE_COLLECTION_EVENTS) {
        const eventId = payload.eventId;
        const _results = nextState.results.map((event) => {
            const _event = event;

            if (!has(_event, 'my_collections')) {
                // eslint-disable-next-line camelcase
                _event.my_collections = {};
            }

            const eventCollections =
                _event['my_collections'].collections || null;

            if (payload.shouldAdd && event.id === eventId) {
                if (eventCollections && isEmpty(eventCollections)) {
                    eventCollections.push({ id: payload.collectionId });
                } else {
                    _event.my_collections.collections = [
                        { id: payload.collectionId },
                    ];
                }
            } else if (!payload.shouldAdd && event.id === eventId) {
                _event.my_collections.collections = eventCollections.filter(
                    (result) => result.id !== payload.collectionId,
                );
            }

            return _event;
        });

        nextState.results = _results;
    }

    return nextState;
};

const browseState = (state = {}, { type, payload }) => {
    let nextState = state;

    if (type === UPDATE_TAB_KEY) {
        nextState = {
            tabKey: payload.tabKey,
            tabKeyLabel: payload.tabKeyLabel,
        };
    }

    return nextState;
};

const seasonal = (state = {}, { type, payload }) => {
    let nextState = state;

    if (type === UPDATE_LOCATION) {
        nextState = getSeasonalContent({ placeId: payload.placeId });
    }

    return nextState;
};

const editorialCollections = (state = [], { type, payload }) => {
    let nextState = state;

    if (type === SET_EDITORIAL_COLLECTIONS) {
        nextState = payload;
    }

    return nextState;
};

export default combineReducers({
    entityContext,
    entities,
    flatBucket,
    browseState,
    seasonal,
    editorialCollections,
});
